// react
import { MouseEvent, ReactNode, useState } from 'react';

// types
import { Document, LoadingStatus } from '../../../@types';

// mui
import { Grid, Link, Typography } from '@mui/material';

// ui
import { Loading } from '../../ui/Loading/Loading';

// utils
import { downloadFile } from '../../../utils/files';

// props
export interface LinkDocumentProps {
	children?: ReactNode;
	document: Document;
	[x: string]: unknown;
}

export const LinkDocument = ({ children, document: doc, ...props }: LinkDocumentProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [status, setStatus] = useState<LoadingStatus>('DEFAULT');

	// vars
	let content = children;

	if (!content) {
		content = (
			<Grid container spacing={1}>
				<Grid item>{doc.name}</Grid>
				{status === 'LOADING' && (
					<Grid item>
						<Loading size="xs" />
					</Grid>
				)}
			</Grid>
		);
	}

	const handleClick = async (e: MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();

		if (status === 'LOADING') {
			return;
		}

		setStatus('LOADING');

		try {
			const reqGetDoc = await fetch(
				`${process.env.REACT_APP_API_URL}/documents/${doc.id}?fields=blob,id,metadata,url`,
				{
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				}
			);

			const resGetDoc = await reqGetDoc.json();

			if (resGetDoc.error) {
				throw resGetDoc.error;
			}

			if (resGetDoc.data) {
				const docFetched = resGetDoc.data[0];

				// decode base64 using fetch API
				const response = await fetch(`data:${docFetched.metadata.type};base64,${docFetched.blob}`);
				const blob = await response.blob();

				downloadFile({
					data: blob,
					name: docFetched.name || doc.name,
					type: docFetched.metadata.type,
				});
			}
		} catch (error) {
			console.error('Error fetching document:', error);
		} finally {
			setStatus('DEFAULT');
		}
	};

	// Render direct download link if document has blob
	if (doc.blob && doc.metadata.type) {
		return (
			<Link
				download={doc.name}
				fontWeight={600}
				href={`data:${doc.metadata.type};base64,${doc.blob}`}
				sx={{ wordBreak: 'break-word' }}
				underline="hover"
				{...props}>
				{content}
			</Link>
		);
	}

	// Render link that fetches document on click if no blob but has id
	if (doc.id) {
		return (
			<Link
				component="button"
				fontWeight={600}
				onClick={handleClick}
				sx={{ wordBreak: 'break-word' }}
				type="button"
				underline="hover"
				{...props}>
				{content}
			</Link>
		);
	}

	// Render plain text if no blob or id
	return <Typography component="span">{content}</Typography>;
};
